import React, { useState, useEffect, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-moment';
import moment from 'moment';
import { AsOfTodayButton, ExportButton, FromTrackReleaseButton, StyledSelect, TimeRangeButton } from './compare-track style';

Chart.register(...registerables);

const abbreviateNumber = (value) => {
  if (value >= 1000000) {
    return (value / 1000000).toFixed(1) + 'M';
  } else if (value >= 1000) {
    return (value / 1000).toFixed(1) + 'k';
  } else {
    return value.toString();
  }
};

const TrackStatsChart = ({ trackData }) => {
  const legendContainerRef = useRef(null);
  const [data, setData] = useState({ labels: [], datasets: [] });
  const [timeRange, setTimeRange] = useState('all');
  const [minDate, setMinDate] = useState(null);
  const [maxDate, setMaxDate] = useState(null);
  const [dataType, setDataType] = useState('Spotify Streams');
  const [selected1, setSelected1] = useState(false);
  const [selected2, setSelected2] = useState(false);
  const [selected3, setSelected3] = useState(false);
  const [selected4, setSelected4] = useState(false);
  const [selectedAll, setSelectedAll] = useState(false);
  const [selectedT, setSelectedT] = useState(false);
  const [selectedA, setSelectedA] = useState(true);
  const [showTracksList, setShowTracksList] = useState(false);
  const [selectedTracks, setSelectedTracks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredTracks, setFilteredTracks] = useState([]);
  const [updatedData, setUpdatedData] = useState([]);

  useEffect(() => {
    setUpdatedData([]);
    setSelectedTracks([]);
    setFilteredTracks([]);
    if (trackData && Array.isArray(trackData.obj)) {
      const initialUpdatedData = trackData.obj.map((dataset, index) => ({ ...dataset, select: index < 5, }));
      setUpdatedData(initialUpdatedData);
    }
  },
    [trackData]);

  const colors = [
    'rgb(231, 134, 92)',
    'rgb(224, 144, 152)',
    'rgb(102, 79, 199)',
    'rgb(11, 164, 218)',
    'rgb(129, 203, 152)',
    'rgb(255, 51, 51)',
    'rgb(255, 255, 0)',
    'rgb(229, 204, 255)',
    'rgb(0, 0, 0)',
    'rgb(35, 228, 196)',
  ];

  const determineTimeUnit = (labels) => {
    const timestamps = labels.map(label => moment(label, 'MMM DD, YYYY').valueOf());
    const minTimestamp = Math.min(...timestamps);
    const maxTimestamp = Math.max(...timestamps);
    const differenceInYears = moment(maxTimestamp).diff(moment(minTimestamp), 'years', true);

    if (differenceInYears >= 10) {
      return 'year';
    } else if (differenceInYears >= 1) {
      return 'quarter';
    } else if (moment(maxTimestamp).diff(moment(minTimestamp), 'months', true) >= 1) {
      return 'month';
    } else {
      return 'day';
    }
  };

  const determineDateFormat = (labels) => {
    const timeUnit = determineTimeUnit(labels);
    switch (timeUnit) {
      case 'year':
        return 'YYYY';
      case 'quarter':
        return 'MMM YYYY';
      default:
        return 'MMM DD, YYYY';
    }
  };

  useEffect(() => {
    if (updatedData.length > 0 && Array.isArray(updatedData)) {
      const legendContainer = legendContainerRef.current;
      if (legendContainer) {
        legendContainer.innerHTML = '';

        const titleContainer = document.createElement('div');
        titleContainer.style.display = 'flex';
        titleContainer.style.alignItems = 'center';
        titleContainer.style.padding = '5px 10px';
        titleContainer.style.paddingLeft = '0px';
        titleContainer.style.borderRadius = '5px';
        titleContainer.style.marginBottom = '5px';
        titleContainer.style.justifyContent = 'space-between';

        const title = document.createElement('h3');
        title.textContent = `${updatedData.filter(dataset => dataset.select).length} Tracks`;
        title.style.marginBottom = '10px';
        title.style.fontSize = '12px';

        const add = document.createElement('button');
        add.textContent = 'Add Tracks';
        add.style.marginBottom = '10px';
        add.style.fontSize = '12px';
        add.style.background = 'none';
        add.style.border = 'none';
        add.style.cursor = 'pointer';
        add.addEventListener('click', () => {
          setShowTracksList(true);
          handleSearch({ target: { value: '' } });
        });

        titleContainer.appendChild(title);
        titleContainer.appendChild(add);
        legendContainer.appendChild(titleContainer);

        const tracksToShow = updatedData.filter(dataset => dataset.select || selectedTracks.some(track => track.name === dataset.name));

        tracksToShow.forEach((dataset, index) => {
          const legendItem = document.createElement('div');
          legendItem.style.display = 'flex';
          legendItem.style.alignItems = 'center';
          legendItem.style.padding = '5px 10px';
          legendItem.style.paddingLeft = '0px';
          legendItem.style.backgroundColor = '#f5f5f5';
          legendItem.style.borderRadius = '5px';
          legendItem.style.marginBottom = '5px';
          legendItem.style.borderLeft = '5px solid ' + colors[index % colors.length];
          legendItem.style.justifyContent = 'space-between';

          const secoundcontainer = document.createElement('div');
          secoundcontainer.style.display = 'flex';
          secoundcontainer.style.alignItems = 'center';
          secoundcontainer.style.paddingLeft = '0px';

          const img = document.createElement('img');
          img.src = dataset.image_url;
          img.style.width = '30px';
          img.style.height = '30px';
          img.style.marginRight = '10px';
          img.style.borderRadius = '5px';
          img.style.marginLeft = '5px';

          const labelDateContainer = document.createElement('div');
          labelDateContainer.style.display = 'flex';
          labelDateContainer.style.flexDirection = 'column';
          labelDateContainer.style.marginBottom = '5px';

          const label = document.createElement('span');
          label.textContent = dataset.name;
          label.style.color = '#000';
          label.style.fontSize = '12px';
          label.style.width = '100%';

          const date = document.createElement('span');
          date.textContent = 'Released ' + moment(dataset.album[0]?.release_date).format(determineDateFormat(data.labels));
          date.style.color = '#b5bdc9';
          date.style.fontSize = '12px';

          labelDateContainer.appendChild(label);
          labelDateContainer.appendChild(date);



          const deselectButton = document.createElement('button');
          deselectButton.textContent = "x";
          deselectButton.style.backgroundColor = 'rgb(215, 220, 221)';
          deselectButton.style.border = 'none';
          deselectButton.style.borderRadius = '50%';
          deselectButton.style.width = '18px';
          deselectButton.style.height = '18px';
          deselectButton.style.display = 'flex';
          deselectButton.style.justifyContent = 'center';
          deselectButton.style.cursor = 'pointer';
          deselectButton.style.fontSize = '12px';
          deselectButton.style.color = 'rgb(245, 245, 245)';
          deselectButton.addEventListener('mouseover', () => {
            deselectButton.style.backgroundColor = 'rgb(181, 189, 201)'
          });
          deselectButton.addEventListener('mouseout', () => {
            deselectButton.style.backgroundColor = 'rgb(215, 220, 221)';
          });

          deselectButton.addEventListener('click', () => {
            const updatedDataCopy = updatedData.map(item => {
              if (item.name === dataset.name) {
                return { ...item, select: false };
              }
              return item;
            });
            setUpdatedData(updatedDataCopy);
            setSelectedTracks(prevTracks => prevTracks.filter(track => track.name !== dataset.name));
          });



          legendItem.addEventListener('click', () => {
            if (!dataset.select && filteredTracks.some(track => track.name === dataset.name)) {
              setSelectedTracks(prevTracks => [...prevTracks, dataset]);
              setShowTracksList(false);
            }
          });

          secoundcontainer.appendChild(img);
          secoundcontainer.appendChild(labelDateContainer);
          legendItem.appendChild(secoundcontainer);
          legendItem.appendChild(deselectButton);

          legendContainer.appendChild(legendItem);
        });
      }

      setData({ labels: [], datasets: updatedData });
    }
  }, [updatedData, selectedTracks]);

  useEffect(() => {
    if (updatedData && Array.isArray(updatedData)) {
      const filteredDatasets = updatedData.filter(dataset => dataset.select);

      filteredDatasets.forEach(dataset => {
        dataset.data.sort((a, b) => moment(a.timestp).valueOf() - moment(b.timestp).valueOf());
      });

      let filteredData = {
        labels: [],
        datasets: filteredDatasets.map((dataset, index) => ({
          label: dataset.name,
          data: dataset.data.map(entry => ({
            x: moment(entry.timestp).valueOf(),
            y: entry.value,
          })).filter(entry => {
            switch (timeRange) {
              case '1m':
                return moment(entry.x).isSameOrAfter(moment().subtract(30, 'days'), 'day');
              case '3m':
                return moment(entry.x).isSameOrAfter(moment().subtract(3, 'months'), 'day');
              case '6m':
                return moment(entry.x).isSameOrAfter(moment().subtract(6, 'months'), 'day');
              case '1y':
                return moment(entry.x).isSameOrAfter(moment().subtract(1, 'years'), 'day');
              default:
                return true;
            }
          }),
          fill: false,
          borderColor: colors[index % colors.length],
          pointRadius: 1,
          pointHoverRadius: 5,
          pointBackgroundColor: colors[index % colors.length],
        })),
      };

      const timestamps = filteredData.datasets.flatMap(dataset =>
        dataset.data.map(entry => moment(entry.x).format('MMM DD, YYYY'))
      );
      filteredData.labels = [...new Set(timestamps)];
      setData(filteredData);

      const minTimestamp = Math.min(...timestamps.map(ts => moment(ts, 'MMM DD, YYYY').valueOf()));
      const maxTimestamp = Math.max(...timestamps.map(ts => moment(ts, 'MMM DD, YYYY').valueOf()));
      setMinDate(moment(minTimestamp).format('MMM DD, YYYY'));
      setMaxDate(moment(maxTimestamp).format('MMM DD, YYYY'));
    }
  }, [updatedData, timeRange]);

  const handleTimeRangeChange = (range) => {
    setSelected1(range === '1m');
    setSelected2(range === '3m');
    setSelected3(range === '6m');
    setSelected4(range === '1y');
    setSelectedAll(range === 'all');
    setTimeRange(range);
  };

  const handleChange = (range) => {
    setSelectedT(range === 'Track');
    setSelectedA(range === 'Today');
  };

  const handleDataTypeChange = (event) => {
    setDataType(event.target.value);
  };

  const handleSearch = (event) => {
    const searchTerm = event.target.value.toLowerCase().trim();
    setSearchTerm(searchTerm);

    const filtered = searchTerm === '' ?
      updatedData.filter(dataset => !dataset.select) :
      updatedData.filter(dataset =>
        !dataset.select && dataset.name.toLowerCase().includes(searchTerm)
      );

    setFilteredTracks(filtered);
  };

  const handleAddTrack = (track) => {
    const updatedTrack = { ...track, select: true };
    const updatedDataCopy = [...updatedData];
    const index = updatedDataCopy.findIndex(t => t.name === track.name);
    if (index !== -1) {
      updatedDataCopy[index] = updatedTrack;
      setUpdatedData(updatedDataCopy);
    }
    setSelectedTracks(prevTracks => [...prevTracks.filter(t => t.name !== track.name), updatedTrack]);
    setShowTracksList(false);
    setSearchTerm('');
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          title: function (tooltipItems) {
            return dataType;
          },
          label: function (tooltipItem) {
            const dataset = updatedData.find(d => d.name === tooltipItem.dataset.label);
            const value = tooltipItem.raw.y;
            const datasetLabel = dataset.name;

            const valueM = abbreviateNumber(value);
            return `${datasetLabel}: ${valueM}`;
          },
          afterLabel: function (tooltipItem) {
            const date = tooltipItem.raw.x;
            return moment(date).format('MMM DD, YYYY');
          },
        },
      },
    },
    scales: {
      x: {
        type: 'time',
        time: {
          unit: determineTimeUnit(data.labels),
          min: minDate ? moment(minDate).startOf('day').valueOf() : undefined,
          max: maxDate ? moment(maxDate).endOf('day').valueOf() : undefined,
          displayFormats: {
            day: 'MMM DD, YYYY',
            week: 'MMM DD, YYYY',
            month: 'MMM YYYY',
            quarter: 'MMM YYYY',
            year: 'YYYY',
          },
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return abbreviateNumber(value);
          },
        },
      },
    },
    hover: {
      mode: 'index',
      intersect: false,
    },
  };

  const exportToCSV = () => {
    const rows = [];
    const header = ["Track", "Date", "Value"];
    rows.push(header.join(","));

    data.datasets.forEach(dataset => {
      dataset.data.forEach(entry => {
        const row = [dataset.label, moment(entry.x).format("MM-DD-YYYY"), entry.y];
        rows.push(row.join(","));
      });
    });

    const csvContent = "data:text/csv;charset=utf-8," + rows.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "track_stats.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <div style={{ fontSize: "12px", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ color: "#1d2b38", marginRight: "15px", fontWeight: "800" }}>Compare Tracks</div>
          <StyledSelect onChange={handleDataTypeChange}>
            <option value="Spotify Streams">Spotify Streams</option>
            <option disabled value="Spotify Popularity">Spotify Popularity</option>
            <option disabled value="Spotify Playlist Count">Spotify Playlist Count</option>
            <option disabled value="Spotify Playlist Reach">Spotify Playlist Reach</option>
            <option disabled value="Youtube Video Views">Youtube Video Views</option>
            <option disabled value="Apple Music Playlist Count">Apple Music Playlist Count</option>
            <option disabled value="Deezer Playlist Count">Deezer Playlist Count</option>
            <option disabled value="Amazon Playlist Count">Amazon Playlist Count</option>
            <option disabled value="Pandora Streams">Pandora Streams</option>
            <option disabled value="Genius Views">Genius Views</option>
          </StyledSelect>
        </div>

        <div style={{ fontSize: "12px", display: "flex", alignItems: "left" }}>
          <div style={{ fontSize: "12px", display: "flex", justifyContent: "space-between", alignItems: "center", border: "1px solid #58667e", borderRadius: "8px", marginRight: "15px" }}>
            {/*<FromTrackReleaseButton selected={selectedT} onClick={() => handleChange("Track")}>From Track Release</FromTrackReleaseButton>*/}
            <AsOfTodayButton selected={selectedA} onClick={() => handleChange("Today")}>As of Today</AsOfTodayButton>
          </div>
          <div style={{ border: "1px solid #58667e", borderRadius: "8px", marginRight: "15px" }}>
            <TimeRangeButton selected={selected1} onClick={() => handleTimeRangeChange('1m')}>1M</TimeRangeButton>
            <TimeRangeButton selected={selected2} onClick={() => handleTimeRangeChange('3m')}>3M</TimeRangeButton>
            <TimeRangeButton selected={selected3} onClick={() => handleTimeRangeChange('6m')}>6M</TimeRangeButton>
            <TimeRangeButton selected={selected4} onClick={() => handleTimeRangeChange('1y')}>1Y</TimeRangeButton>
            <TimeRangeButton selected={selectedAll} onClick={() => handleTimeRangeChange('all')}>All</TimeRangeButton>
          </div>


          <ExportButton style={{ border: "1px solid #58667e", borderRadius: "8px", marginRight: "15px" }} onClick={() => exportToCSV()}>Export</ExportButton>

        </div>

      </div>
      <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ width: "250px" }} ref={legendContainerRef}></div>
        {showTracksList && (
          <div style={{ position: 'absolute', top: '-5%', marginTop: '20px', padding: '10px', backgroundColor: 'white', borderRadius: '5px', boxShadow: " 0 2px 4px rgba(0, 0, 0, 0.1)" }}>
            <input
              type="text"
              placeholder="Search tracks..."
              value={searchTerm}
              onChange={handleSearch}
              style={{ marginBottom: '10px', width: '95%', padding: '5px', border: '1px solid #ccc', borderRadius: '5px' }}
            />
            <p style={{ marginBottom: "5px" }}>Track Suggestions</p>
            {filteredTracks.map((dataset, index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '10px', cursor: 'pointer', backgroundColor: '#f5f5f5', borderRadius: "5px", padding: "5px" }}
                onClick={() => handleAddTrack(dataset)}>
                <img src={dataset.image_url} alt={dataset.name} style={{ width: '30px', height: '30px', borderRadius: '5px', marginRight: '10px' }} />
                <div>
                  <p style={{ margin: '0', fontSize: '14px', fontWeight: 'bold', color: selectedTracks.some(track => track.name === dataset.name) ? '#1d2b38' : '#333' }}>{dataset.name}</p>
                  <p style={{ margin: '0', fontSize: '12px', color: '#666' }}>Released {moment(dataset.release_date).format(determineDateFormat(data.labels))}</p>
                </div>
              </div>
            ))}
          </div>
        )}
        <div style={{ flex: 1 }}>
          <Line data={data} options={options} />
        </div>
      </div>
      <div style={{ display: "block", textAlign: "right", backgroundColor: "#f5f7f9", padding: "10px" }}>
        <p style={{ width: "100%", marginRight: "15px" }}>{dataType} from {minDate} - {maxDate}</p>
      </div>
    </>
  );
};

export default TrackStatsChart;
